import { request } from './request';
import * as PATHS from '@/constants/path';
import { URLParamBuilder } from '@/utils/functions';
import { GetRecentServiceRequestsRequest } from '@/typings/api/getRecentServiceRequest';
import { GetCustomersRequest } from '@/typings/api/getCustomers';

/** Fetch Recent Service Requests */

type FetchRecentServiceRequestsOptions = {
  params: GetRecentServiceRequestsRequest['parameters']
  url?: string
  query?: GetRecentServiceRequestsRequest['query']
}

export function fetchRecentServiceRequests({ params, query = {}, url }: FetchRecentServiceRequestsOptions) {
  let finalUrl: string;
  if (url) {
    const parsed = new URL(url);
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        parsed.searchParams.set(key, String(value));
      }
    });
    finalUrl = parsed.toString();
  } else {
    finalUrl = URLParamBuilder(PATHS.GET_RECENT_SERVICE_REQUEST_API_PATH, {
      params,
      queryString: query,
    });
  }

  return request<GetRecentServiceRequestsRequest['response']>(finalUrl, {
    method: 'GET',
    absoluteURL: !!url,
  });
}


export function fetchCalendarViewServiceRequests({
                                                   query = {},
                                                 }) {
  const url = URLParamBuilder(PATHS.GET_SERVICE_REQUESTS, {
    queryString: query,
  });
  return request<GetCustomersRequest['response']>(url, {
    method: 'GET',
  });
}

export function fetchMultiBookingsListRequest() {
  const url = URLParamBuilder(PATHS.GET_MULTI_BOOKING_LIST);
  return request(url, {
    method: 'GET',
  });
}

export function fetchCalendarViewCalendarEvents({
                                                  query = {},
                                                }) {
  const url = URLParamBuilder(PATHS.GET_CALENDAR_EVENTS, {
    queryString: query,
  });
  return request<GetCustomersRequest['response']>(url, {
    method: 'GET',
  });
}

export function fetchCalendarUnavailabilityRequest({
                                                     query = {},
                                                   }) {
  const url = URLParamBuilder(PATHS.GET_CALENDAR_UNAVAILABILITY, {
    queryString: query,
  });
  return request(url, {
    method: 'GET',
  });
}


export function rescheduleServiceRequestRequest({
                                                  params,
                                                  data = {},
                                                  options = { timeChanged: false },
                                                }) {
  const timeChanged = options?.timeChanged || false;
  if (!timeChanged){
    delete data.start;
    delete data.end;
  }

  const url = URLParamBuilder(timeChanged ? PATHS.RESCHEDULE_SERVICE_REQUEST : PATHS.EDIT_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: timeChanged ? 'POST' : 'PATCH',
    data,
  });
}


export function editServiceRequestRequest({
                                            params,
                                            data = {},
                                          }) {
  const url = URLParamBuilder(PATHS.EDIT_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: 'PATCH',
    data,
  });
}

export function fetchSingleServiceRequestRequest({
                                            params,
                                            data = {},
                                          }) {
  const url = URLParamBuilder(PATHS.EDIT_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: 'GET',
  });
}

export function fetchSingleCalendarEventRequest({
                                            params,
                                          }) {
  const url = URLParamBuilder(PATHS.EDIT_CALENDAR_EVENT, {
    params,
  });
  return request(url, {
    method: 'GET',
  });
}

export function declineServiceRequestRequest({
                                               data = {},
                                               params = {},
                                             }) {
  const url = URLParamBuilder(PATHS.DECLINE_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: 'POST',
    data,
  });
}

export function cancelServiceRequestRequest({
                                              params = {},
                                              data = {},
                                            }: { params?: Record<string, any>; data?: Record<string, any> } = {}) {
  const url = URLParamBuilder(PATHS.CANCEL_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: 'POST',
    data,
  });
}

export function confirmServiceRequestRequest({
                                               params = {},
                                             }) {
  const url = URLParamBuilder(PATHS.CONFIRM_SERVICE_REQUEST, {
    params,
  });
  return request(url, {
    method: 'POST',
  });
}


export function bookServiceRequestRequest({
                                            params,
                                            data = {},
                                          }) {
  const url = URLParamBuilder(PATHS.BOOK_APPOINTMENT, {
    params,
  });
  return request(url, {
    method: 'POST',
    data,
  });
}


export function addCalendarEventRequest({
                                          data = {},
                                        }) {
  return request(PATHS.ADD_CALENDAR_EVENT, {
    method: 'POST',
    data,
  });
}

/** Create a recurring calendar-event series (POST /calendar-events/recurring). */
export function addRecurringCalendarEventRequest({
                                                    data = {},
                                                  }) {
  return request(PATHS.ADD_RECURRING_CALENDAR_EVENT, {
    method: 'POST',
    data,
  });
}


export function editCalendarEventRequest({
                                           data = {},
                                           params = {},
  options ={}
                                         }) {
  const url = URLParamBuilder(PATHS.EDIT_CALENDAR_EVENT, {
    params,
  });
  return request(url, {
    method: 'PUT',
    data,
  });
}

export function patchCalendarEventRequest({
                                            data = {},
                                            params = {},
                                          }) {
  const url = URLParamBuilder(PATHS.EDIT_CALENDAR_EVENT, {
    params,
  });
  return request(url, {
    method: 'PATCH',
    data,
  });
}

export function deleteCalendarEventRequest({
                                             data = undefined,
                                             params = {},
                                             query = {},
                                           }) {
  const url = URLParamBuilder(PATHS.DELETE_CALENDAR_EVENT, {
    params,
    // The recurring scope (apply_to) is a query parameter on DELETE, not a body
    // field — a plain single-event delete stays queryless as before.
    queryString: query,
  });
  return request(url, {
    method: 'DELETE',
    // Callers that pass no body get a bodyless DELETE, as before the send_email option.
    ...(data ? { data } : {}),
  });
}

export function deleteServiceRequestRequest({ params }: { params: { serviceRequestId: string } }) {
  const url = URLParamBuilder(PATHS.EDIT_SERVICE_REQUEST, { params });
  return request(url, { method: 'DELETE' });
}

export function bulkDeleteServiceRequestsRequest({ ids }: { ids: string[] }) {
  return request(PATHS.GET_SERVICE_REQUESTS, { method: 'DELETE', data: { ids } });
}

/**
 * Bulk cancel, confirm, or set extra fields on multiple service requests.
 *
 * @param action - "cancel", "confirm", or "set_extra_fields"
 * @param ids - Array of service request hash IDs (max 100)
 * @param value - Optional value for set_extra_fields action
 */
export function bulkServiceRequestAction({
  action,
  ids,
  value,
}: {
  action: 'cancel' | 'confirm' | 'set_extra_fields';
  ids: string[];
  value?: any;
}) {
  return request(PATHS.BULK_SERVICE_REQUESTS, {
    method: 'POST',
    data: { action, ids, value },
  });
}
